import React, { useState } from 'react';
import { jsPDF } from 'jspdf'; // 👈 Import jsPDF

const ImageUpload = () => {
  const [images, setImages] = useState([]);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleImage(file);
  };

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) handleImage(file);
  };

  const handleImage = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      setFileSizeError(true);
      return;
    }

    setFileSizeError(false);
    setIsFileUploaded(true);

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const newImage = {
        name: file.name,
        url: url,
        width: img.width,
        height: img.height,
        size: file.size,
        format: file.type.split('/')[1],
      };
      setImages((prevImages) => [...prevImages, newImage]);
    };
    img.src = url;
  };

  const convertImageFormat = (imageUrl, format) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const dataUrl = canvas.toDataURL(`image/${format}`);
        const convertedImage = dataURLToBlob(dataUrl);
        const objectURL = URL.createObjectURL(convertedImage);
        resolve(objectURL);
      };
      img.src = imageUrl;
    });
  };

  const dataURLToBlob = (dataUrl) => {
    const byteString = atob(dataUrl.split(',')[1]);
    const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uintArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uintArray[i] = byteString.charCodeAt(i);
    }

    return new Blob([uintArray], { type: mimeString });
  };

  const downloadAsPDF = async (imageUrl, fileName) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const pdf = new jsPDF({
        orientation: img.width > img.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [img.width, img.height],
      });

      pdf.addImage(img, 'JPEG', 0, 0, img.width, img.height);
      pdf.save(`${fileName}.pdf`);
    };

    img.src = imageUrl;
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 px-4 md:px-8 py-12" id="upload">
      <div className="bg-white p-4 md:p-8 rounded-lg shadow-xl w-full max-w-6xl">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-6 md:mb-8 text-gray-800">Upload Your Image</h2>

        <div
          className={`border-2 ${isFileUploaded ? 'border-green-500' : 'border-dashed border-gray-400'} 
            p-6 md:p-12 rounded-lg text-center bg-gray-100 hover:bg-gray-200 transition-all duration-300 ease-in-out shadow-md ${
              isFileUploaded ? 'shadow-lg' : ''
            }`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <p className="text-gray-600 mb-4 text-base md:text-lg">Drag and drop an image here, or</p>
          <label
            htmlFor="file-upload"
            className="bg-blue-500 text-white px-4 py-2 md:px-6 md:py-3 rounded cursor-pointer hover:bg-blue-600 transition duration-200 text-base md:text-lg"
          >
            Browse Files
          </label>
          <input
            type="file"
            id="file-upload"
            className="hidden"
            onChange={handleChange}
            accept="image/*"
          />
        </div>

        {fileSizeError && (
          <p className="text-red-500 mt-4 text-center text-base md:text-lg">
            File is too large. Please upload a file less than 5MB.
          </p>
        )}

        {images.length > 0 && (
          <div className="mt-8 overflow-x-auto">
            <h3 className="text-xl md:text-2xl font-semibold mb-4">Uploaded Images</h3>
            <table className="w-full table-auto border-collapse text-sm md:text-base">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-4 md:px-6 py-2 md:py-3 text-left text-gray-700">Image Name</th>
                  <th className="border px-4 md:px-6 py-2 md:py-3 text-left text-gray-700">Size</th>
                  <th className="border px-4 md:px-6 py-2 md:py-3 text-left text-gray-700">Dimensions</th>
                  <th className="border px-4 md:px-6 py-2 md:py-3 text-left text-gray-700">Download Options</th>
                </tr>
              </thead>
              <tbody>
                {images.map((image, index) => (
                  <tr key={index} className="border-b">
                    <td className="border px-4 md:px-6 py-2 md:py-4 text-gray-800 break-all">{image.name}</td>
                    <td className="border px-4 md:px-6 py-2 md:py-4 text-gray-800">
                      {(image.size / 1024 / 1024).toFixed(2)} MB
                    </td>
                    <td className="border px-4 md:px-6 py-2 md:py-4 text-gray-800">
                      {image.width} x {image.height}
                    </td>
                    <td className="border px-4 md:px-6 py-2 md:py-4 text-gray-800">
                      <div className="flex flex-wrap gap-2">
                        {['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'heif', 'pdf'].map((format) => (
                          <button
                            key={format}
                            onClick={async () => {
                              if (format === 'pdf') {
                                await downloadAsPDF(image.url, image.name.split('.')[0]);
                              } else {
                                const convertedUrl = await convertImageFormat(image.url, format);
                                const downloadLink = document.createElement('a');
                                downloadLink.href = convertedUrl;
                                downloadLink.download = `${image.name.split('.')[0]}.${format}`;
                                downloadLink.click();
                              }
                            }}
                            className="text-blue-500 hover:underline text-xs md:text-sm"
                          >
                            {format.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
